import React, { useImperativeHandle, useState } from 'react'
import { createPortal } from 'react-dom'

import { TooltipProvider } from '@/components/ui/tooltip'
import { getShapeScreenRect } from '@/lib/editor/floating-canvas-chrome-bounds'
import {
  ToolbarGripButton,
  ToolbarResetButton,
  stopPropagation,
  useBlockBrowserZoomRef,
  useDraggableToolbarAnchor,
  useFloatingChromePosition,
} from '@/components/app/editor/tools/floating-toolbar'

import { ModeToggleButton } from './components/mode-toggle-button'
import { PointTypeButton } from './components/point-type-button'
import { TH, TW } from './constants/node-edit-toolbar-dimensions'
import { computeNodeEditToolbarPos } from './helpers/node-edit-toolbar-placement'
import type { NodeEditToolbarProps, NodeEditToolbarRef } from './types/node-edit-toolbar-props'

export type { NodeEditToolbarProps, NodeEditToolbarRef } from './types/node-edit-toolbar-props'

export const NodeEditToolbar = React.forwardRef<NodeEditToolbarRef, NodeEditToolbarProps>(
  (
    {
      shape,
      scale,
      canvasRef,
      isPenEditMode,
      onSelectMode,
      onPenMode,
      onReset,
      resetDisabled,
      pointType,
      onPointTypeChange,
    },
    ref
  ) => {
    const [hoveredButton, setHoveredButton] = useState<'mode' | 'pointType' | 'reset' | null>(null)
    const { rootRef, setRootRef } = useBlockBrowserZoomRef()
    const pos = useFloatingChromePosition(canvasRef, shape, scale, computeNodeEditToolbarPos)
    const {
      displayPos,
      isDragging,
      handleDragStart,
      handleDragMove,
      handleDragEnd,
      cancelActiveDrag,
    } = useDraggableToolbarAnchor({
      shape,
      scale,
      canvasRef,
      pos,
      width: TW,
      height: TH,
      // The un-rotated shape rect, unlike the crop/text-on-path bars' chrome rect.
      getShapeRect: getShapeScreenRect,
      revertOnCancel: false,
    })

    useImperativeHandle(ref, () => ({ cancelActiveDrag }), [cancelActiveDrag])

    if (!displayPos) return null

    const clearHover = () => setHoveredButton(null)

    return createPortal(
      <div
        ref={setRootRef}
        style={{ position: 'fixed', left: displayPos.x, top: displayPos.y, zIndex: 9999 }}
        onPointerDown={stopPropagation}
        onPointerUp={stopPropagation}
        onClick={stopPropagation}
      >
        <TooltipProvider delayDuration={0}>
          <div
            className="flex items-center gap-2 rounded-[14px] border border-black/[0.07] bg-white"
            style={{
              paddingTop: 8,
              paddingRight: 8,
              paddingBottom: 8,
              paddingLeft: 4,
              boxShadow: '0 4px 20px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)',
            }}
          >
            {/* Drag handle — pulled toward the left edge and close to the divider so it reads as
              a pull tab rather than a regular toolbar button. */}
            <ToolbarGripButton
              isDragging={isDragging}
              onDragStart={handleDragStart}
              onDragMove={handleDragMove}
              onDragEnd={handleDragEnd}
            />

            <div className="-ml-1 h-5 w-px flex-shrink-0 bg-black/[0.12]" />

            {/* Mode toggle buttons */}
            <div className="flex gap-1">
              <ModeToggleButton
                isPenEditMode={isPenEditMode}
                onSelectMode={onSelectMode}
                onPenMode={onPenMode}
                isHovered={hoveredButton === 'mode'}
                onHoverStart={() => setHoveredButton('mode')}
                onHoverEnd={clearHover}
                tooltipContainer={rootRef.current}
              />

              <PointTypeButton
                pointType={pointType}
                onPointTypeChange={onPointTypeChange}
                isHovered={hoveredButton === 'pointType'}
                onHoverStart={() => setHoveredButton('pointType')}
                onHoverEnd={clearHover}
                tooltipContainer={rootRef.current}
              />
            </div>

            {/* Reset */}
            <ToolbarResetButton
              onReset={onReset}
              disabled={resetDisabled}
              enabledTooltip="Reset to original shape"
              isHovered={hoveredButton === 'reset'}
              onHoverStart={() => setHoveredButton('reset')}
              onHoverEnd={clearHover}
              tooltipContainer={rootRef.current}
            />
          </div>
        </TooltipProvider>
      </div>,
      document.body
    )
  }
)

NodeEditToolbar.displayName = 'NodeEditToolbar'
